// ProTillReconciliation.jsx
// Route: /pro/till-reconciliation
// Mirrors real-world "Cash Up" / "Till Balance" workflow (cf. Salon Iris)
// Loads expected takings from pro_sales for a date, manager enters actuals,
// variance is shown, Confirm closes the till → writes to income + employee_income.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Box, Paper, Typography, Button, TextField, Alert,
  CircularProgress, Divider, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PointOfSale as TillIcon,
} from '@mui/icons-material';

const NAVY   = '#2f3542';
const fmt    = (n) => parseFloat(n || 0).toFixed(2);
const fmtInt = (n) => parseInt(n || 0, 10);

const VAR_CHIP = (variance) => {
  const v = parseFloat(variance);
  if (Math.abs(v) < 0.01) return { label: 'Balanced', color: 'success' };
  if (v > 0)               return { label: `+£${fmt(v)} over`, color: 'warning' };
  return                          { label: `-£${fmt(Math.abs(v))} short`, color: 'error' };
};

function SummaryRow({ label, value, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color={bold ? 'text.primary' : 'text.secondary'}
        fontWeight={bold ? 700 : 400}>{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>£{fmt(value)}</Typography>
    </Box>
  );
}

function ActualInput({ label, value, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Typography variant="body2" sx={{ minWidth: 120 }}>{label}</Typography>
      <TextField
        value={value}
        onChange={e => onChange(e.target.value)}
        size="small"
        type="number"
        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 100 } }}
        InputProps={{
          startAdornment: (
            <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }}>£</Typography>
          ),
        }}
      />
    </Box>
  );
}

export default function ProTillReconciliation() {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate]   = useState(today);
  const [loading,      setLoading]        = useState(false);
  const [closing,      setClosing]        = useState(false);
  const [error,        setError]          = useState(null);
  const [summary,      setSummary]        = useState(null);   // GET /till-summary response
  const [closeResult,  setCloseResult]    = useState(null);   // POST /till-close response

  // Actual amounts entered by manager
  const [actualCash,    setActualCash]    = useState('');
  const [actualCard,    setActualCard]    = useState('');
  const [actualVoucher, setActualVoucher] = useState('');

  // Confirm dialog
  const [confirmOpen,  setConfirmOpen]    = useState(false);

  // ── Load summary ───────────────────────────────────────────────────────
  const loadSummary = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    setSummary(null);
    setCloseResult(null);
    // Pre-fill actuals to expected amounts as a convenience
    setActualCash('');
    setActualCard('');
    setActualVoucher('');
    try {
      const res = await api.get('/pro-diary/till-summary', { params: { date } });
      setSummary(res.data);
      // Pre-fill actuals with expected totals so manager only needs to correct discrepancies
      setActualCash(fmt(res.data.totals.cash_total));
      setActualCard(fmt(res.data.totals.card_total));
      setActualVoucher(fmt(res.data.totals.voucher_total));
    } catch (e) {
      if (e.response?.status === 404) {
        setError('No open sales found for this date.');
      } else {
        setError('Could not load till summary. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(selectedDate);
  }, [selectedDate, loadSummary]);

  // ── Variance calculations ──────────────────────────────────────────────
  const expectedCash    = parseFloat(summary?.totals?.cash_total    || 0);
  const expectedCard    = parseFloat(summary?.totals?.card_total    || 0);
  const expectedVoucher = parseFloat(summary?.totals?.voucher_total || 0);
  const varCash    = parseFloat(actualCash    || 0) - expectedCash;
  const varCard    = parseFloat(actualCard    || 0) - expectedCard;
  const varVoucher = parseFloat(actualVoucher || 0) - expectedVoucher;
  const totalVariance = varCash + varCard + varVoucher;

  // ── Close till ─────────────────────────────────────────────────────────
  const handleClose = async () => {
    setConfirmOpen(false);
    setClosing(true);
    setError(null);
    try {
      const res = await api.post('/pro-diary/till-close', {
        date:           selectedDate,
        actual_cash:    parseFloat(actualCash    || 0),
        actual_card:    parseFloat(actualCard    || 0),
        actual_voucher: parseFloat(actualVoucher || 0),
      });
      setCloseResult(res.data);
      setSummary(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to close till. Please try again.');
    } finally {
      setClosing(false);
    }
  };

  // ── Render: success state ──────────────────────────────────────────────
  if (closeResult) {
    const t = closeResult.totals;
    return (
      <Box sx={{ maxWidth: 680, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Till Closed — {selectedDate}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {closeResult.sales_closed} sale{closeResult.sales_closed !== 1 ? 's' : ''} across{' '}
            {closeResult.stylists} stylist{closeResult.stylists !== 1 ? 's' : ''} posted to income records.
          </Typography>

          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant="overline" color="text.secondary">Variance Summary</Typography>
            {[
              ['Cash',    t.expected_cash,    t.actual_cash,    t.variance_cash],
              ['Card',    t.expected_card,    t.actual_card,    t.variance_card],
              ['Voucher', t.expected_voucher, t.actual_voucher, t.variance_voucher],
            ].map(([label, exp, act, vari]) => {
              const chip = VAR_CHIP(vari);
              return (
                <Box key={label} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  py: 1, borderBottom: '1px solid', borderColor: 'divider',
                }}>
                  <Typography variant="body2">{label}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Expected £{fmt(exp)} · Actual £{fmt(act)}
                    </Typography>
                    <Chip label={chip.label} color={chip.color} size="small" />
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={() => navigate('/pro/diary')}>
              Back to Diary
            </Button>
            <Button variant="contained" sx={{ bgcolor: NAVY }}
              onClick={() => { setCloseResult(null); loadSummary(selectedDate); }}>
              View Another Date
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1.5, md: 3 } }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pro/diary')}>
          Diary
        </Button>
        <TillIcon sx={{ color: NAVY }} />
        <Typography variant="h5" fontWeight={700}>Cash Up</Typography>
      </Box>

      {/* Date selector */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
          Reconciliation date:
        </Typography>
        <TextField
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          size="small"
          inputProps={{ max: today }}
          sx={{ width: 180 }}
        />
        {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
      </Paper>

      {error && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

          {/* ── LEFT: Expected takings ── */}
          <Box>

            {/* Per-stylist breakdown */}
            <Paper sx={{ mb: 2.5 }}>
              <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Expected Takings — {summary.totals.sale_count} client{summary.totals.sale_count !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {summary.stylists.length === 0 ? (
                <Box sx={{ px: 2.5, pb: 2 }}>
                  <Typography variant="body2" color="text.secondary">No open sales for this date.</Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '11px', color: 'text.secondary' } }}>
                      <TableCell>Stylist</TableCell>
                      <TableCell align="center">Clients</TableCell>
                      <TableCell align="right">Services</TableCell>
                      <TableCell align="right">Retail</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.stylists.map(s => (
                      <TableRow key={s.employee_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {s.first_name} {s.last_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{s.client_count}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">£{fmt(s.service_total)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">£{fmt(s.product_total)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>£{fmt(s.grand_total)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>

            {/* Payment method breakdown */}
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Expected Payment Split
              </Typography>
              <SummaryRow label="Services"     value={summary.totals.service_total} />
              <SummaryRow label="Retail"        value={summary.totals.product_total} />
              {parseFloat(summary.totals.discount_total) > 0 && (
                <SummaryRow label="Discounts (−)" value={-summary.totals.discount_total} />
              )}
              {parseFloat(summary.totals.tip_total) > 0 && (
                <SummaryRow label="Tips"          value={summary.totals.tip_total} />
              )}
              <Divider sx={{ my: 1 }} />
              <SummaryRow label="Cash"    value={summary.totals.cash_total} />
              <SummaryRow label="Card"    value={summary.totals.card_total} />
              <SummaryRow label="Voucher" value={summary.totals.voucher_total} />
              <Divider sx={{ my: 1 }} />
              <SummaryRow label="Total takings" value={summary.totals.grand_total} bold />
              {parseFloat(summary.totals.vat_total) > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Includes £{fmt(summary.totals.vat_total)} VAT
                </Typography>
              )}
            </Paper>
          </Box>

          {/* ── RIGHT: Actual amounts + variance ── */}
          <Box>
            <Paper sx={{ p: 2.5, mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Actual Amounts Counted
                </Typography>
                <Tooltip title="Enter the amounts you physically counted / read from your card terminal.">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </Tooltip>
              </Box>

              <ActualInput label="Cash counted"     value={actualCash}    onChange={setActualCash} />
              <ActualInput label="Card terminal"    value={actualCard}    onChange={setActualCard} />
              <ActualInput label="Vouchers redeemed" value={actualVoucher} onChange={setActualVoucher} />
            </Paper>

            {/* Variance panel */}
            <Paper sx={{ p: 2.5, mb: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Variance
              </Typography>

              {[
                ['Cash',    expectedCash,    actualCash,    varCash],
                ['Card',    expectedCard,    actualCard,    varCard],
                ['Voucher', expectedVoucher, actualVoucher, varVoucher],
              ].map(([label, exp, act, vari]) => {
                const chip = VAR_CHIP(vari);
                return (
                  <Box key={label} sx={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', py: 0.75,
                    borderBottom: '1px solid', borderColor: 'divider',
                  }}>
                    <Box>
                      <Typography variant="body2">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expected £{fmt(exp)} · Actual £{fmt(act)}
                      </Typography>
                    </Box>
                    <Chip label={chip.label} color={chip.color} size="small" />
                  </Box>
                );
              })}

              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1,
                         display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={600}>Net variance</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {Math.abs(totalVariance) >= 0.01 && (
                    <WarningIcon sx={{ fontSize: 18, color: totalVariance < 0 ? 'error.main' : 'warning.main' }} />
                  )}
                  <Typography variant="body1" fontWeight={700}
                    color={Math.abs(totalVariance) < 0.01 ? 'success.main'
                          : totalVariance < 0 ? 'error.main' : 'warning.main'}>
                    {totalVariance >= 0 ? '+' : ''}£{fmt(totalVariance)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Confirm button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={closing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={closing || summary.totals.sale_count === 0}
              sx={{ py: 1.75, bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' }, fontSize: '15px' }}
            >
              Confirm &amp; Close Till
            </Button>

            {Math.abs(totalVariance) >= 0.01 && (
              <Alert severity="warning" sx={{ mt: 1.5, fontSize: '12px' }}>
                There is a variance of £{fmt(Math.abs(totalVariance))}. You can still confirm —
                actual amounts will be recorded as entered.
              </Alert>
            )}
          </Box>
        </Box>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Till Close</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will post today's sales to income records and mark all {summary?.totals?.sale_count || 0} sale(s) as closed.
            This cannot be undone.
          </Typography>
          <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
            <SummaryRow label="Expected total" value={summary?.totals?.grand_total || 0} />
            <SummaryRow label="Cash entered"   value={actualCash} />
            <SummaryRow label="Card entered"   value={actualCard} />
            <SummaryRow label="Voucher entered" value={actualVoucher} />
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={700}>Net variance</Typography>
              <Typography variant="body2" fontWeight={700}
                color={Math.abs(totalVariance) < 0.01 ? 'success.main' : 'warning.main'}>
                {totalVariance >= 0 ? '+' : ''}£{fmt(totalVariance)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleClose} variant="contained"
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}>
            Confirm Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}